import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import { Loader2, CheckCircle2, MessageCircle } from 'lucide-react';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    tratamiento: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, tratamiento: value }));
  };

  const validateForm = () => {
    if (!formData.nombre.trim()) {
      toast.error('Por favor, ingresa tu nombre');
      return false;
    }
    if (!formData.email.trim() || !formData.email.includes('@')) {
      toast.error('Por favor, ingresa un email válido');
      return false;
    }
    if (!formData.telefono.trim()) {
      toast.error('Por favor, ingresa tu teléfono con WhatsApp');
      return false;
    }
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const message = `Hola, mi nombre es ${formData.nombre}. Estoy interesado en el tratamiento ${formData.tratamiento || 'No especificado'}. Mi email es ${formData.email} y mi teléfono es ${formData.telefono}.`;
      const encodedMessage = encodeURIComponent(message);
      const whatsappUrl = `https://wa.me/34637285137?text=${encodedMessage}`;

      // Redirigir a WhatsApp
      window.location.href = whatsappUrl;

      setIsSuccess(true);
      toast.success('¡Abriendo WhatsApp!');

      // Reset form
      setFormData({
        nombre: '',
        email: '',
        telefono: '',
        tratamiento: ''
      });
    } catch (error) {
      console.error('Error:', error);
      toast.error('Hubo un problema. Inténtalo de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact-form" className="pt-20 pb-10 px-4 sm:px-6 lg:px-8">
      <div className="container mx-auto max-w-2xl">
        <Card className="shadow-elegant border-border/50 bg-gradient-card overflow-hidden">
          <CardHeader className="text-center pb-6 bg-gradient-subtle">
            <CardTitle className="text-3xl sm:text-4xl font-bold text-foreground mb-2">
              Reserva tu valoración <span className="text-gradient-primary">GRATUITA</span>
            </CardTitle>
            <p className="text-base text-muted-foreground">
              Habla con nosotros por WhatsApp directamente
            </p>
          </CardHeader>

          <CardContent className="p-6 sm:p-8">
            {!isSuccess ? (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Nombre */}
                <div className="space-y-2">
                  <Label htmlFor="nombre" className="text-sm font-medium text-foreground">
                    Nombre completo <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="nombre"
                    name="nombre"
                    type="text"
                    placeholder="María García"
                    value={formData.nombre}
                    onChange={handleChange}
                    required
                    className="border-border focus:ring-primary focus:border-primary"
                  />
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium text-foreground">
                    Email <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="maria@ejemplo.com"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="border-border focus:ring-primary focus:border-primary"
                  />
                </div>

                {/* Teléfono */}
                <div className="space-y-2">
                  <Label htmlFor="telefono" className="text-sm font-medium text-foreground">
                    Teléfono con WhatsApp <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="telefono"
                    name="telefono"
                    type="tel"
                    placeholder="+34 600 000 000"
                    value={formData.telefono}
                    onChange={handleChange}
                    required
                    className="border-border focus:ring-primary focus:border-primary"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Incluye el código de país (ej: +34 para España)
                  </p>
                </div>

                {/* Tratamiento */}
                <div className="space-y-2">
                  <Label htmlFor="tratamiento" className="text-sm font-medium text-foreground">
                    Tratamiento de interés
                  </Label>
                  <Select value={formData.tratamiento} onValueChange={handleSelectChange}>
                    <SelectTrigger className="border-border focus:ring-primary focus:border-primary">
                      <SelectValue placeholder="Selecciona un tratamiento" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="facial">Tratamientos Faciales</SelectItem>
                      <SelectItem value="corporal">Tratamientos Corporales</SelectItem>
                      <SelectItem value="medicina">Medicina Estética</SelectItem>
                      <SelectItem value="postoperatorio">Postoperatorio</SelectItem>
                      <SelectItem value="no_segura">No estoy segura</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-primary hover:shadow-glow transition-all duration-300 text-primary-foreground font-semibold py-6 text-base rounded-lg"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    'Quiero mi valoración gratuita'
                  )}
                </Button>

                <p className="text-xs text-center text-muted-foreground">
                  Al enviar este formulario, aceptas que te contactemos por WhatsApp para coordinar tu valoración gratuita.
                </p>
              </form>
            ) : (
              <div className="text-center py-8 animate-scale-in">
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-primary-muted flex items-center justify-center animate-pulse-soft">
                  <CheckCircle2 className="w-10 h-10 text-primary" />
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-4">
                  ¡Ya casi está!
                </h3>
                <p className="text-base text-muted-foreground mb-6 max-w-md mx-auto">
                  Por favor, haz clic en el botón de abajo para enviar los datos de tu valoración por WhatsApp.
                </p>
                <div className="flex flex-col items-center gap-4">
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      const message = `Hola, mi nombre es ${formData.nombre}. Estoy interesado en el tratamiento ${formData.tratamiento || 'No especificado'}. Mi email es ${formData.email} y mi teléfono es ${formData.telefono}.`;
                      window.location.href = `https://wa.me/34637285137?text=${encodeURIComponent(message)}`;
                    }}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#25D366] text-white hover:bg-[#128C7E] transition-colors shadow-lg font-semibold"
                  >
                    <MessageCircle className="w-5 h-5" />
                    Enviar mensaje por WhatsApp
                  </a>
                </div>
                <Button
                  onClick={() => setIsSuccess(false)}
                  variant="outline"
                  className="mt-8"
                >
                  Enviar otro formulario
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Additional Info */}
        <div className="mt-8 text-center">
          <p className="text-sm text-muted-foreground mb-4">
            ¿Prefieres llamar directamente?
          </p>
          <a
            href="tel:+34637285137"
            className="inline-flex items-center gap-2 text-primary hover:text-primary/80 transition-colors font-medium"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
            </svg>
            670 26 17 58
          </a>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;